import { Banner, Card, ErrorState, Loading, Metric, MetricRow, SectionHeader, Table } from '../components.jsx'
import { useApi } from '../hooks.js'
import { Link } from 'react-router-dom'
import PageHeader from './PageHeader.jsx'

/**
 * Setup and tools.
 *
 * **Every legacy admin page is now a native console route.** The seven static pages under
 * `static/admin/` each carried their own login form and their own in-memory token, so moving between them meant
 * signing in again — the loudest complaint about this surface. Users, vendors, crane entry, master import and
 * escalation were ported first; **reports was the one left behind**, still linked out to
 * `/admin/reports.html`, so every single download asked for credentials while the banner below claimed nothing
 * here leaves the console. `pages/Reports.jsx` closed that, and this page links to it.
 *
 * The static pages remain served for now: `AdminPageEnumIntegrationTest` pins their dropdowns to the server's
 * enums and they are what loaded the plant's data before the ports existed. Nothing in the console links to
 * them any more.
 */
export default function Admin() {
  const reports = useApi('/reports')
  const users = useApi('/admin/users')

  /*
   * `/admin/users` returns a PAGE object: { users, page, size, totalUsers, totalPages }.
   *
   * The first version read `users.data?.content || users.data`, guessing Spring's `Page` shape. Neither key
   * matched, so `staff` fell through to the wrapper OBJECT — and calling `.filter()` on an object throws
   * "filter is not a function", which React turns into a blank white screen for the entire page. Reported as
   * "the moment I click Setup & tools the screen goes white".
   *
   * `Array.isArray` rather than `?? []`: a shape change should render an empty band, never crash the page
   * again. And the lesson this project keeps relearning — I asserted the response shape from memory instead
   * of calling the endpoint. One curl showed `users`.
   */
  const page = users.data
  const staff = Array.isArray(page?.users) ? page.users : []
  // `totalUsers` is the fleet-wide figure; `staff` is one page of 50. Showing `staff.length` would silently
  // under-report the moment a plant has more accounts than fit on a page.
  const totalUsers = page?.totalUsers ?? staff.length

  return (
    <>
      <PageHeader title="Setup & tools" subtitle="Accounts, master data, escalation and exports" />

      {/*
        Nothing under Administration leaves the console any more.
        
        This banner previously warned that two tools opened separately and asked for a second sign-in. Both are
        native now, so the warning went with them — a caveat left in place after it stops being true is worse
        than none, because the next one gets ignored too.
      */}
      <Banner kind="success">
        <span>
          Every administration tool is part of this console — one sign-in, all in the sidebar. The API browser
          is embedded rather than rebuilt, so it stays generated from the running backend.
        </span>
      </Banner>

      <SectionHeader hint="All native to this console">Administration</SectionHeader>
      <div className="grid-2">
        {TOOLS.map((t) => (
          <Card key={t.to} title={t.title} subtitle={t.blurb}>
            {/* An in-app route, not target="_blank": staying in the console is the whole point of porting these. */}
            <Link className="btn btn-primary" to={t.to}>Open {t.title.toLowerCase()}</Link>
          </Card>
        ))}
      </div>

      <SectionHeader hint="Generated on the server, as XLSX or CSV">Reports</SectionHeader>
      <Card>
        {reports.loading && <Loading rows={4} />}
        {reports.error && <ErrorState error={reports.error} onRetry={reports.reload} />}
        {reports.data && (
          <Table
            keyOf={(r) => r.key}
            columns={[
              { key: 'title', header: 'Report', render: (r) => <strong>{r.title}</strong> },
              { key: 'description', header: 'What it contains' },
              {
                key: 'params',
                header: 'Filters',
                // `params`, not `parameters`. The server's `ReportDefinitionResponse` field is `params`, so this
                // column read undefined and printed "None" for all ten reports — including the three that take
                // a date window. Another response shape asserted from memory rather than from the endpoint,
                // which is the same mistake that produced the white screen this page had (see the note above).
                render: (r) => (r.params?.length ? r.params.join(', ') : <span className="muted">None</span>),
              },
              {
                key: 'get',
                header: '',
                render: (r) => (
                  <span className="cell-row">
                    {/*
                      An in-app route now, not a link to `/admin/reports.html`.

                      That static page carries its own login form and its own in-memory token, so every download
                      asked for credentials again — reported as "in the reports section, why is every report
                      asking for login?" while the banner at the top of this very page claimed nothing here
                      leaves the console. My comment here defended the link on the grounds that the old page
                      "already handles the token"; `api()` has had a `raw: true` mode with the single-flight
                      refresh in it the whole time, which is what `pages/Reports.jsx` now uses.
                    */}
                    <Link className="btn" to="/reports">Download</Link>
                  </span>
                ),
              },
            ]}
            rows={reports.data}
          />
        )}
      </Card>
    </>
  )
}

/**
 * The administration tools, all native.
 *
 * Every entry here is an in-console route now. The four that were links — crane import, single crane entry,
 * escalation and the API browser — were deferred on the reasoning that they "work and trials are close", which
 * measured nothing: three were forms against endpoints that already existed, and the fourth is generated
 * documentation that only ever needed framing. Signing in repeatedly to do one round of administration was the
 * thing that made this surface feel like a utility.
 */
const TOOLS = [
  {
    to: '/admin/import',
    title: 'Crane master import',
    blurb: 'Upload the Excel template for a whole fleet. Dry run first — the preview shows every row that would be added, updated or rejected before anything is written.',
  },
  {
    to: '/admin/cranes',
    title: 'Single crane entry',
    blurb: 'Add or correct one crane. The form is generated from the same schema the importer uses, so the two cannot disagree.',
  },
  {
    to: '/admin/users',
    title: 'User accounts',
    blurb: 'Create operators, engineers, safety officers and management logins; reset a password; deactivate someone who has left. Includes the audit of who did each.',
  },
  {
    to: '/admin/vendors',
    title: 'Vendors',
    blurb: 'AMC contractors and external firms. A firm must exist here before a vendor login can be created for it.',
  },
  {
    to: '/admin/escalation',
    title: 'Escalation & approvals',
    blurb: 'Who is told when a complaint goes unanswered, edited as a chain of roles and wait times rather than raw JSON. Plus the register of who signed off which safety closure.',
  },
  {
    to: '/admin/api',
    title: 'API documentation',
    blurb: 'The live OpenAPI browser, generated by the backend. Embedded rather than rebuilt, so it cannot drift from what the server accepts.',
  },
]
